import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import AthleteContextHeader from '../components/AthleteContextHeader';
import AthleteSearchFilter from '../components/AthleteSearchFilter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StaffFinance = ({ athleteId: propAthleteId }) => {
  const navigate = useNavigate();
  const isScopedMode = !!propAthleteId;
  const [athletes, setAthletes] = useState([]);
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [downloadingInvoicePdf, setDownloadingInvoicePdf] = useState(null);

  const [newInvoice, setNewInvoice] = useState({
    invoice_number: '',
    description: '',
    amount: '',
    due_date: '',
    status: 'pending'
  });

  const [newPayment, setNewPayment] = useState({
    amount_paid: '',
    date_paid: '',
    payment_method: ''
  });

  useEffect(() => {
    if (propAthleteId) {
      axios.get(`${API}/athletes/${propAthleteId}`).then(res => {
        setAthletes([res.data]);
        selectAthlete(res.data);
        setLoading(false);
      }).catch(() => { toast.error('Failed to fetch athlete'); setLoading(false); });
    } else {
      fetchMyAthletes();
    }
  }, [propAthleteId]);

  const downloadInvoicePdf = async (invoiceId, invoiceNumber) => {
    setDownloadingInvoicePdf(invoiceId);
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}/pdf`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice_${invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Invoice PDF downloaded');
    } catch (error) {
      toast.error('Failed to download invoice PDF');
    } finally {
      setDownloadingInvoicePdf(null);
    }
  };

  const fetchMyAthletes = async () => {
    try {
      const response = await axios.get(`${API}/athletes`);
      setAthletes(response.data);
    } catch (error) {
      toast.error('Failed to fetch athletes');
    } finally {
      setLoading(false);
    }
  };

  const selectAthlete = async (athlete) => {
    setSelectedAthlete(athlete);
    await fetchFinanceData(athlete.id);
  };

  const fetchFinanceData = async (athleteId) => {
    try {
      const [invoicesRes, summaryRes] = await Promise.all([
        axios.get(`${API}/invoices/athlete/${athleteId}`),
        axios.get(`${API}/finance/athlete/${athleteId}/summary`)
      ]);
      
      setInvoices(invoicesRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to fetch finance data:', error);
    }
  };

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    if (!selectedAthlete) return;

    try {
      await axios.post(`${API}/invoices`, {
        athlete_id: selectedAthlete.id,
        ...newInvoice,
        amount: parseFloat(newInvoice.amount)
      });
      toast.success('Invoice created successfully');
      setInvoiceDialogOpen(false);
      setNewInvoice({
        invoice_number: '',
        description: '',
        amount: '',
        due_date: '',
        status: 'pending'
      });
      fetchFinanceData(selectedAthlete.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    }
  };

  const openPaymentDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setNewPayment({
      amount_paid: '',
      date_paid: new Date().toISOString().split('T')[0],
      payment_method: ''
    });
    setPaymentDialogOpen(true);
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!selectedInvoice) return;

    try {
      await axios.post(`${API}/payments`, {
        invoice_id: selectedInvoice.id,
        amount_paid: parseFloat(newPayment.amount_paid),
        date_paid: newPayment.date_paid,
        payment_method: newPayment.payment_method
      });
      toast.success('Payment recorded successfully');
      setPaymentDialogOpen(false);
      fetchFinanceData(selectedAthlete.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      paid: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      pending: 'bg-amber-100 text-amber-800 border-amber-200',
      overdue: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[status] || colors.pending;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8">
        <AthleteContextHeader athleteId={propAthleteId} moduleName="Finance" />
        <div className="mb-6 flex justify-between items-start">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Finance & Payments
            </h2>
            <p className="text-gray-600 dark:text-gray-500 mt-1">Manage invoices and track payments</p>
          </div>
          <Button
            onClick={() => navigate('/finance/reports')}
            variant="outline"
            className="gap-2"
            data-testid="view-reports"
          >
            📊 View Reports & Export
          </Button>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Athlete Selection Sidebar */}
          {!isScopedMode && (
          <div className="col-span-3">
            <Card data-testid="athlete-list">
              <CardHeader>
                <CardTitle className="text-lg">My Athletes</CardTitle>
              </CardHeader>
              <CardContent>
                <AthleteSearchFilter athletes={athletes} compact searchFirst>
                  {(filtered) => (
                    <div className="space-y-1 max-h-[60vh] overflow-y-auto">
                      {filtered.map((athlete) => (
                        <button
                          key={athlete.id}
                          onClick={() => selectAthlete(athlete)}
                          className={`w-full text-left px-3 py-2 rounded-lg transition-colors text-sm ${
                            selectedAthlete?.id === athlete.id
                              ? 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200'
                              : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                          data-testid={`athlete-${athlete.id}`}
                        >
                          <span>{athlete.full_name}</span>
                          {athlete.squad && <span className="text-[10px] text-gray-400 ml-1">{athlete.squad}</span>}
                        </button>
                      ))}
                      {filtered.length === 0 && <p className="text-xs text-gray-400 text-center py-3">No athletes match</p>}
                    </div>
                  )}
                </AthleteSearchFilter>
              </CardContent>
            </Card>
          </div>
          )}

          {/* Finance Information */}
          <div className={isScopedMode ? "col-span-12" : "col-span-9"}>
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
              </div>
            ) : !selectedAthlete ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-gray-500 dark:text-gray-500">Select an athlete to view financial information</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {/* Financial Summary Card */}
                {summary && (
                  <Card data-testid="finance-summary-card">
                    <CardHeader>
                      <CardTitle>Financial Summary - {selectedAthlete.full_name}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-4 gap-4">
                        <div className="bg-blue-50 p-4 rounded-lg">
                          <p className="text-sm text-blue-700 font-medium">Total Invoiced</p>
                          <p className="text-2xl font-bold text-blue-900" data-testid="total-invoiced">
                            {formatCurrency(summary.total_invoiced)}
                          </p>
                        </div>
                        <div className="bg-emerald-50 p-4 rounded-lg">
                          <p className="text-sm text-emerald-700 font-medium">Total Paid</p>
                          <p className="text-2xl font-bold text-emerald-900" data-testid="total-paid">
                            {formatCurrency(summary.total_paid)}
                          </p>
                        </div>
                        <div className="bg-amber-50 p-4 rounded-lg">
                          <p className="text-sm text-amber-700 font-medium">Pending</p>
                          <p className="text-2xl font-bold text-amber-900" data-testid="total-pending">
                            {formatCurrency(summary.total_pending)}
                          </p>
                        </div>
                        <div className="bg-red-50 p-4 rounded-lg">
                          <p className="text-sm text-red-700 font-medium">Outstanding</p>
                          <p className="text-2xl font-bold text-red-900" data-testid="outstanding-balance">
                            {formatCurrency(summary.outstanding_balance)}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Invoices Card */}
                <Card>
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <CardTitle>Invoices</CardTitle>
                      <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
                        <DialogTrigger asChild>
                          <Button data-testid="create-invoice-button">+ Create Invoice</Button>
                        </DialogTrigger>
                        <DialogContent data-testid="invoice-dialog">
                          <DialogHeader>
                            <DialogTitle>Create Invoice</DialogTitle>
                          </DialogHeader>
                          <form onSubmit={handleCreateInvoice}>
                            <div className="space-y-4 py-4">
                              <div>
                                <Label htmlFor="invoice_number">Invoice Number</Label>
                                <Input
                                  id="invoice_number"
                                  value={newInvoice.invoice_number}
                                  onChange={(e) => setNewInvoice({...newInvoice, invoice_number: e.target.value})}
                                  placeholder="e.g., INV-2026-001"
                                  required
                                  data-testid="invoice-number-input"
                                />
                              </div>
                              <div>
                                <Label htmlFor="description">Description</Label>
                                <Textarea
                                  id="description"
                                  value={newInvoice.description}
                                  onChange={(e) => setNewInvoice({...newInvoice, description: e.target.value})}
                                  placeholder="Academy fees, training costs, etc."
                                  required
                                  data-testid="description-input"
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <Label htmlFor="amount">Amount (€)</Label>
                                  <Input
                                    id="amount"
                                    type="number"
                                    step="0.01"
                                    value={newInvoice.amount}
                                    onChange={(e) => setNewInvoice({...newInvoice, amount: e.target.value})}
                                    required
                                    data-testid="amount-input"
                                  />
                                </div>
                                <div>
                                  <Label htmlFor="due_date">Due Date</Label>
                                  <Input
                                    id="due_date"
                                    type="date"
                                    value={newInvoice.due_date}
                                    onChange={(e) => setNewInvoice({...newInvoice, due_date: e.target.value})}
                                    required
                                    data-testid="due-date-input"
                                  />
                                </div>
                              </div>
                            </div>
                            <DialogFooter>
                              <Button type="submit" data-testid="submit-invoice">Create Invoice</Button>
                            </DialogFooter>
                          </form>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {invoices.length === 0 ? (
                      <p className="text-gray-500 dark:text-gray-500 text-center py-8">No invoices yet</p>
                    ) : (
                      <div className="space-y-3" data-testid="invoices-list">
                        {invoices.map((invoice) => (
                          <Card key={invoice.id} className="border-2" data-testid={`invoice-${invoice.id}`}>
                            <CardHeader>
                              <div className="flex justify-between items-start">
                                <div>
                                  <div className="flex items-center space-x-2">
                                    <CardTitle className="text-lg">{invoice.invoice_number}</CardTitle>
                                    <Badge className={getStatusBadge(invoice.status)} data-testid={`invoice-status-${invoice.id}`}>
                                      {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                                    </Badge>
                                  </div>
                                  <CardDescription className="mt-1">{invoice.description}</CardDescription>
                                </div>
                                <div className="text-right">
                                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{formatCurrency(invoice.amount)}</p>
                                  <p className="text-sm text-gray-500 dark:text-gray-500">Due: {new Date(invoice.due_date).toLocaleDateString()}</p>
                                </div>
                              </div>
                            </CardHeader>
                            <CardContent>
                              {invoice.status !== 'paid' && (
                                <Button 
                                  size="sm" 
                                  onClick={() => openPaymentDialog(invoice)}
                                  data-testid={`record-payment-${invoice.id}`}
                                >
                                  Record Payment
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => downloadInvoicePdf(invoice.id, invoice.invoice_number)}
                                disabled={downloadingInvoicePdf === invoice.id}
                                data-testid={`download-invoice-pdf-${invoice.id}`}
                              >
                                {downloadingInvoicePdf === invoice.id ? '...' : '📄 PDF'}
                              </Button>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Payment Dialog */}
                <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
                  <DialogContent data-testid="payment-dialog">
                    <DialogHeader>
                      <DialogTitle>Record Payment</DialogTitle>
                      <DialogDescription>
                        Invoice: {selectedInvoice?.invoice_number} - {selectedInvoice && formatCurrency(selectedInvoice.amount)}
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleRecordPayment}>
                      <div className="space-y-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="amount_paid">Amount Paid (€)</Label>
                            <Input
                              id="amount_paid"
                              type="number"
                              step="0.01"
                              value={newPayment.amount_paid}
                              onChange={(e) => setNewPayment({...newPayment, amount_paid: e.target.value})}
                              required
                              data-testid="amount-paid-input"
                            />
                          </div>
                          <div>
                            <Label htmlFor="date_paid">Date Paid</Label>
                            <Input
                              id="date_paid"
                              type="date"
                              value={newPayment.date_paid}
                              onChange={(e) => setNewPayment({...newPayment, date_paid: e.target.value})}
                              required
                              data-testid="date-paid-input"
                            />
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="payment_method">Payment Method</Label>
                          <Select
                            value={newPayment.payment_method}
                            onValueChange={(value) => setNewPayment({...newPayment, payment_method: value})}
                          >
                            <SelectTrigger data-testid="payment-method-select">
                              <SelectValue placeholder="Select payment method" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                              <SelectItem value="Cash">Cash</SelectItem>
                              <SelectItem value="Credit Card">Credit Card</SelectItem>
                              <SelectItem value="Debit Card">Debit Card</SelectItem>
                              <SelectItem value="MB Way">MB Way</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button type="submit" data-testid="submit-payment">Record Payment</Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default StaffFinance;
